import React, { useMemo } from 'react';
import { useConversation } from '../../context/ConversationProvider';
import { useTimeTick } from '../../hooks/useTimeTick';
import OnlineStatusDot from '../shared/OnlineStatusDot';
import useChatUser from '../../hooks/useChatUser';
import { formatTimestamp } from '../../../../utils/timeUtils';

/**
 * Memoized comparison function for ConversationItem
 * Prevents re-renders when conversation data hasn't changed
 *
 * Note: currentUser is obtained via useConversation() inside the component,
 * so we focus comparison on conversation data and active state
 */
const arePropsEqual = (prevProps, nextProps) => {
    const prevConv = prevProps.conversation;
    const nextConv = nextProps.conversation;

    // Same conversation ID - most important check
    if (prevConv.id !== nextConv.id) return false;

    // Check lastMessageAt timestamp (comparing serialized form for stability)
    const prevLMA = prevConv.lastMessageAt?.toMillis?.() || prevConv.lastMessageAt?.getTime?.() || prevConv.lastMessageAt;
    const nextLMA = nextConv.lastMessageAt?.toMillis?.() || nextConv.lastMessageAt?.getTime?.() || nextConv.lastMessageAt;
    if (prevLMA !== nextLMA) return false;

    // Check preview text and sender (impacts preview display)
    if (prevConv.lastMessageText !== nextConv.lastMessageText) return false;
    if (prevConv.lastMessageSenderName !== nextConv.lastMessageSenderName) return false;

    // Check active state (impacts styling)
    if (prevProps.activeConversationId !== nextProps.activeConversationId) return false;

    // onSelect reference changes frequently, but we skip this check
    // since the parent should memoize its callback (ConversationList optimization)

    return true;
};

const ConversationItem = ({ conversation, activeConversationId, onSelect }) => {
    // Use modular contexts instead of legacy useChat
    const { currentUser } = useConversation();
    // FIX M-1: Removed unused usePresence() — it caused unnecessary re-renders
    // FIX L-10: Use shared timer instead of per-item setInterval
    const timeTick = useTimeTick();

    // Memoize expensive calculations
    const memberIds = useMemo(() =>
        Array.isArray(conversation.memberIds) ? conversation.memberIds : [],
        [conversation.memberIds]
    );

    const primaryMemberId = useMemo(() => {
        if (conversation.type !== 'direct' || memberIds.length !== 2 || !currentUser?.uid) return null;
        return memberIds.find(id => id !== currentUser.uid) || null;
    }, [conversation.type, memberIds, currentUser?.uid]);

    // Use the hook to get user data
    const { user, isOnline } = useChatUser(primaryMemberId);

    // Memoize display values
    const { displayName, photoURL } = useMemo(() => {
        if (conversation.type !== 'direct' || !primaryMemberId) {
            return { displayName: conversation.title || 'Conversation', photoURL: '' };
        }
        if (!user) {
            return { displayName: 'User', photoURL: '' };
        }
        return {
            displayName: user.displayName || user.username || 'User',
            photoURL: user.photoURL || ''
        };
    }, [conversation.type, conversation.title, primaryMemberId, user]);

    // Memoize unread detection & formatted time (depends on timeTick for live updates)
    const { isUnread, formattedTime } = useMemo(() => {
        const lmaRaw = conversation.lastMessageAt;
        const lma = lmaRaw && lmaRaw.toDate ? lmaRaw.toDate() : lmaRaw || null;
        const lsaRaw = currentUser?.uid && conversation.lastSeenAt ? conversation.lastSeenAt[currentUser.uid] : null;
        const lsa = lsaRaw && lsaRaw.toDate ? lsaRaw.toDate() : lsaRaw || null;
        const hasMessages = !!(conversation.lastMessageText || conversation.lastMessagePreview);
        const unread = hasMessages && lma && (!lsa || lma > lsa);
        const time = lma ? formatTimestamp(lma) : '';
        // eslint-disable-next-line no-unused-expressions
        timeTick; // Use timeTick to force re-evaluation for live updates
        return { isUnread: unread, formattedTime: time };
    }, [conversation.lastMessageAt, conversation.lastSeenAt, conversation.lastMessageText, conversation.lastMessagePreview, currentUser?.uid, timeTick]);

    // Memoize sender prefix
    const senderPrefix = useMemo(() => {
        const hasMessages = !!(conversation.lastMessageText || conversation.lastMessagePreview);
        if (!hasMessages) return '';

        const isMine = conversation.lastMessageSenderId === currentUser?.uid;
        if (isMine) return 'You: ';

        if (conversation.type === 'group' && conversation.lastMessageSenderName) {
            const firstName = conversation.lastMessageSenderName.split(' ')[0];
            return `${firstName}: `;
        }

        return '';
    }, [conversation.type, conversation.lastMessageSenderId, conversation.lastMessageSenderName, conversation.lastMessageText, conversation.lastMessagePreview, currentUser?.uid]);

    const lastMessage = conversation.lastMessageText || conversation.lastMessagePreview;
    const isActive = conversation.id === activeConversationId;

    return (
        <button
            type="button"
            className={`chat-list__item ${isActive ? 'chat-list__item--active' : ''} ${isUnread ? 'chat-list__item--unread' : ''}`}
            onClick={() => onSelect(conversation.id)}
        >
            <div className="chat-list__avatar-container">
                {photoURL ? (
                    <img
                        src={photoURL}
                        alt={displayName}
                        className="chat-list__avatar"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <div className="chat-list__avatar chat-list__avatar--placeholder">
                        {(displayName[0] || '?').toUpperCase()}
                    </div>
                )}
                {primaryMemberId && (
                    <span className="chat-list__online-indicator">
                        <OnlineStatusDot online={isOnline} />
                    </span>
                )}
            </div>
            <div className="chat-list__content">
                <div className="chat-list__title">
                    <span className="chat-list__display-name">{displayName}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {formattedTime && (
                            <span className="chat-list__timestamp">{formattedTime}</span>
                        )}
                        {isUnread && (
                            <span className="chat-list__unread-dot" title="Unread message" />
                        )}
                    </div>
                </div>
                {lastMessage && (
                    <div className="chat-list__preview">
                        {senderPrefix && <span className="chat-list__preview-prefix">{senderPrefix}</span>}
                        {lastMessage}
                    </div>
                )}
                {!lastMessage && (
                    <div className="chat-list__preview chat-list__preview--empty">No messages yet</div>
                )}
            </div>
        </button>
    );
};

// Wrap with memo and custom comparison for optimal re-render prevention
export default React.memo(ConversationItem, arePropsEqual);
